/**
 * Phase E — Pure status-derivation helpers for the Win95 status bar.
 *
 * Five cells per Q16:
 *   1. Convex   — green when summary returns; red when null.
 *   2. M365     — green > 5min until token expiry, amber < 5min, red expired.
 *   3. Portal   — green when provisioning status === 'complete'; amber when
 *                 queued / running; red when failed; grey when unknown.
 *   4. Index    — green when docCount > 0; amber when 0 but indexName known;
 *                 grey when no portal slug yet.
 *   5. User     — green whenever signed-in user is identified.
 *
 * Each derivation is a pure function so vitest can pin behaviour without
 * mounting the SPA. The component composes these into a 5-cell footer.
 */

import type { HealthSummary, IndexHealth } from "../../../convex/health";

export type CellState = "green" | "amber" | "red" | "grey";

export interface CellModel {
  state: CellState;
  label: string;
  tooltip: string;
}

export function deriveConvexCell(
  summary: HealthSummary | null | undefined,
): CellModel {
  if (summary === null || summary === undefined) {
    return {
      state: "red",
      label: "Convex",
      tooltip: "Convex query failed or still loading.",
    };
  }
  const url = summary.convexDeploymentUrl;
  const tooltip =
    url.length > 0
      ? `Convex deployment: ${url}`
      : "Convex query OK (deployment URL not exposed).";
  return { state: "green", label: "Convex", tooltip };
}

export interface M365Input {
  /** 'valid' | 'expiring-soon' | 'expired' | 'unknown' from lib/graph/tokenStatus. */
  status: "valid" | "expiring-soon" | "expired" | "unknown";
  expiresOn?: string;
  accountEmail?: string;
  /**
   * K.3.b — When the active portal slug resolves to a `tenantBranding` row
   * with a real `firmSkillsSiteId`, we override the cell with a green
   * "Connected to real SharePoint" state regardless of token status. The
   * server-side app-only Graph token (landed in K.1+K.2) means evaluators
   * don't need a Microsoft sign-in for the SharePoint surface to be real.
   */
  tenantSharePoint?: {
    /** Composite siteId from Graph: `<host>,<site-guid>,<web-guid>`. */
    siteId: string;
    /** SharePoint drive id (`b!...`). Optional — some sites are read-only. */
    driveId: string | null | undefined;
    /** Display name for the portal — surfaces in the cell label + tooltip. */
    displayName: string;
    /** Last-sync timestamp (ms). 0 / undefined when never synced. */
    lastSyncAt?: number;
  } | null;
}

/**
 * K.3.b helper — strict check that a tenantSharePoint payload carries a real
 * SharePoint composite siteId. We want to avoid green-claiming a portal that
 * was provisioned with placeholder ids.
 *
 * A composite Graph siteId looks like:
 *   `areweai.sharepoint.com,24d5c820-...,e9a8...`
 * (host, comma, site guid, comma, web guid).
 *
 * Exported so the test pins the contract.
 */
export function hasRealTenantSharePoint(
  payload: M365Input["tenantSharePoint"],
): boolean {
  if (payload === null || payload === undefined) return false;
  const sid = payload.siteId?.trim() ?? "";
  if (sid.length === 0) return false;
  // Require the canonical 3-part composite to avoid placeholder ids.
  const parts = sid.split(",");
  if (parts.length !== 3) return false;
  if (parts.some((p) => p.trim().length === 0)) return false;
  // Host must look like *.sharepoint.com.
  return /^[a-z0-9-]+\.sharepoint\.com$/i.test(parts[0]?.trim() ?? "");
}

export function deriveM365Cell(input: M365Input): CellModel {
  // K.3.b — proof-of-SharePoint override. If the active portal resolves to
  // a real SharePoint site, the cell goes GREEN regardless of MSAL state,
  // because the SPA uses the server-side app-only Graph token to talk to
  // SharePoint (no user sign-in required).
  if (hasRealTenantSharePoint(input.tenantSharePoint)) {
    const sp = input.tenantSharePoint!;
    const sync =
      sp.lastSyncAt && sp.lastSyncAt > 0
        ? ` · last sync ${new Date(sp.lastSyncAt).toLocaleTimeString()}`
        : "";
    return {
      state: "green",
      label: `M365 · ${sp.displayName}`,
      tooltip: `Connected to real SharePoint · site ${sp.siteId.split(",")[0]}${sync}`,
    };
  }
  switch (input.status) {
    case "valid": {
      const acct = input.accountEmail ? ` (${input.accountEmail})` : "";
      return {
        state: "green",
        label: "M365",
        tooltip: `Microsoft token valid${acct}.`,
      };
    }
    case "expiring-soon": {
      const acct = input.accountEmail ? ` (${input.accountEmail})` : "";
      return {
        state: "amber",
        label: "M365",
        tooltip: `Microsoft token expires within 5 minutes${acct}.`,
      };
    }
    case "expired":
      return {
        state: "red",
        label: "M365",
        tooltip: "Microsoft token expired — click M365 to reconnect.",
      };
    case "unknown":
    default:
      return {
        state: "grey",
        label: "M365",
        // K.3.b — without a real tenantSharePoint we're honestly in demo
        // mode for the SharePoint surface; say so in the tooltip.
        tooltip: input.tenantSharePoint === null
          ? "Demo data — no real SharePoint connection."
          : "Microsoft account not yet connected.",
      };
  }
}

export function derivePortalCell(
  slug: string | null | undefined,
  status: string | null | undefined,
): CellModel {
  if (!slug) {
    return {
      state: "grey",
      label: "Portal · not open",
      // 2026-05-28 — slug now comes from the URL, so "no slug" means
      // the user is on `/` (root desktop), not "they have no
      // portal". Make that explicit so the cell stops looking like
      // "the system forgot your portal."
      tooltip:
        "Not currently inside a portal. Open a portal via /c/<slug>/desktop to bind this desktop to a SharePoint workspace.",
    };
  }
  const baseLabel = `Portal · ${slug}`;
  switch (status) {
    case "complete":
      return {
        state: "green",
        label: baseLabel,
        tooltip: `Your portal ready · /c/${slug} — Repancy-managed SharePoint workspace.`,
      };
    case "running":
      return {
        state: "amber",
        label: baseLabel,
        tooltip: "Provisioning in progress…",
      };
    case "queued":
      return {
        state: "amber",
        label: baseLabel,
        tooltip: "Provisioning queued.",
      };
    case "failed":
      return {
        state: "red",
        label: baseLabel,
        tooltip: "Provisioning failed — see Settings.",
      };
    default:
      return {
        state: "grey",
        label: baseLabel,
        tooltip: `Portal status unknown (${status ?? "n/a"}).`,
      };
  }
}

export function deriveIndexCell(
  index: IndexHealth | null | undefined,
): CellModel {
  if (index === null || index === undefined) {
    return {
      state: "grey",
      label: "Index",
      tooltip: "No portal yet — Azure Search probe unavailable.",
    };
  }
  if (index.indexName.length === 0) {
    return {
      state: "grey",
      label: "Index",
      tooltip: "Index name unresolved.",
    };
  }
  if (index.docCount > 0) {
    const when = index.lastRefreshedAt > 0
      ? new Date(index.lastRefreshedAt).toLocaleTimeString()
      : "unknown time";
    return {
      state: "green",
      label: `Index · ${index.docCount}`,
      tooltip: `Index ${index.indexName} · last refresh ${when}`,
    };
  }
  return {
    state: "amber",
    label: "Index · 0",
    tooltip: `Index ${index.indexName} has no successful ingests yet.`,
  };
}

export interface UserCellInput {
  isSignedIn: boolean;
  displayName?: string;
  email?: string;
}

export function deriveUserCell(input: UserCellInput): CellModel {
  if (!input.isSignedIn) {
    return {
      state: "grey",
      label: "Sign in",
      tooltip: "Not signed in.",
    };
  }
  const displayName =
    input.displayName && input.displayName.length > 0
      ? input.displayName
      : input.email && input.email.length > 0
        ? input.email
        : "Signed in";
  return {
    state: "green",
    label: displayName,
    tooltip: input.email ? `Signed in as ${input.email}` : "Signed in.",
  };
}

/**
 * Win95 status-cell colors. Black-on-light per
 * `feedback_no_light_grey_text`; secondary text stays >=gray-700.
 */
export const CELL_COLORS: Record<CellState, { bg: string; dot: string }> = {
  green: { bg: "#ece9d8", dot: "#008000" },
  amber: { bg: "#ece9d8", dot: "#cc8800" },
  red: { bg: "#ece9d8", dot: "#cc0000" },
  grey: { bg: "#ece9d8", dot: "#808080" },
};
